import { useMemo, useState } from "react";
import { Plus, Library, PenLine, Search } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ROOM_TYPES, getRoomTemplate, getCommonUpgrades } from "@/lib/roomTemplates";
import { effectiveRate } from "@/lib/regionalPresets";
import { useFormatCurrency } from "@/lib/CurrencyContext";

const KIND_OPTIONS = [
  { value: "material", label: "Material" },
  { value: "labour", label: "Labour" },
  { value: "mixed", label: "Mixed" },
];

const UNIT_OPTIONS = ["sqm", "lm", "item", "set", "room", "days", "hours"];

function newId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return "i-" + Math.random().toString(36).slice(2);
}

/**
 * AddItemDialog — lets the user pick a work item from the room-type library
 * (template items + the user's custom items saved in the Cost Library), or
 * build a custom one from scratch. Library rates already account for the
 * user's overrides + currency multiplier.
 */
export default function AddItemDialog({
  open,
  onOpenChange,
  room,                 // { room_type, work_items, ... }
  costLibrary = null,   // { room_rates: {...}, custom_room_items: {...} }
  currency = "USD",
  onAddItem,            // (item) => void
  finishMult = 1,       // applied to library rate so result matches the room's finish level
}) {
  const fmt = useFormatCurrency();
  const [tab, setTab] = useState("library");
  const [search, setSearch] = useState("");

  const overrides = costLibrary?.room_rates?.[room?.room_type] || {};
  const customRoomItems = costLibrary?.custom_room_items?.[room?.room_type] || [];
  const tpl = getRoomTemplate(room?.room_type) || [];
  const tplNames = useMemo(() => new Set(tpl.map((t) => t.name.toLowerCase())), [tpl]);
  const customNames = useMemo(
    () => new Set(customRoomItems.map((c) => (c.name || "").toLowerCase())),
    [customRoomItems]
  );
  const existingNames = useMemo(
    () => new Set((room?.work_items || []).map((w) => (w.name || "").toLowerCase())),
    [room?.work_items]
  );

  // 1) Items native to this room type + the user's custom additions for it.
  const roomLibraryItems = useMemo(() => {
    const fromTpl = tpl.map((t) => {
      const rate = Math.round(effectiveRate(t.baseRate, overrides[t.name], currency) * finishMult);
      return {
        source: "template",
        name: t.name,
        kind: t.kind,
        unit: t.unit,
        rate,
        suggestedQty: typeof t.qty === "function" ? t.qty(Number(room?.size_sqm) || 0) : t.qty,
      };
    });
    const fromCustom = customRoomItems.map((c) => ({
      source: "custom",
      name: c.name,
      kind: c.kind || "material",
      unit: c.unit || "item",
      rate: Math.round(Number(c.rate || 0) * finishMult),
      suggestedQty: 1,
    }));
    return [...fromTpl, ...fromCustom];
  }, [tpl, customRoomItems, overrides, currency, finishMult, room?.size_sqm]);

  // 2) Common upgrades — every item across every room template that ISN'T
  // already in this room type's native template (or the user's custom list
  // for this room). Lets the user, e.g., add Demolition to a laundry room.
  const commonUpgrades = useMemo(() => {
    return getCommonUpgrades()
      .filter(
        (i) =>
          !tplNames.has(i.name.toLowerCase()) &&
          !customNames.has(i.name.toLowerCase())
      )
      .map((i) => ({
        source: "common",
        name: i.name,
        kind: i.kind,
        unit: i.unit,
        rate: Math.round(effectiveRate(i.baseRate, overrides[i.name], currency) * finishMult),
        suggestedQty: 1,
      }));
  }, [tplNames, customNames, overrides, currency, finishMult]);

  const matches = (i, q) => !q || i.name.toLowerCase().includes(q);

  const visibleRoomLibrary = useMemo(() => {
    const q = search.trim().toLowerCase();
    return roomLibraryItems
      .filter((i) => matches(i, q))
      .map((i) => ({ ...i, alreadyAdded: existingNames.has(i.name.toLowerCase()) }));
  }, [roomLibraryItems, search, existingNames]);

  const visibleCommon = useMemo(() => {
    const q = search.trim().toLowerCase();
    return commonUpgrades
      .filter((i) => matches(i, q))
      .map((i) => ({ ...i, alreadyAdded: existingNames.has(i.name.toLowerCase()) }));
  }, [commonUpgrades, search, existingNames]);

  // Custom-item form state
  const [draft, setDraft] = useState({
    name: "",
    kind: "material",
    unit: "item",
    quantity: 1,
    rate: 0,
  });

  const resetAndClose = () => {
    onOpenChange(false);
    setDraft({ name: "", kind: "material", unit: "item", quantity: 1, rate: 0 });
    setSearch("");
    setTab("library");
  };

  const addFromLibrary = (libItem) => {
    onAddItem({
      id: newId(),
      name: libItem.name,
      kind: libItem.kind,
      unit: libItem.unit,
      quantity: libItem.suggestedQty || 1,
      rate: libItem.rate,
    });
    resetAndClose();
  };

  const addCustom = () => {
    if (!draft.name.trim()) return;
    onAddItem({
      id: newId(),
      name: draft.name.trim(),
      kind: draft.kind,
      unit: draft.unit,
      quantity: Number(draft.quantity) || 1,
      rate: Number(draft.rate) || 0,
    });
    resetAndClose();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-white border-black/[0.08] text-[#27272A] sm:max-w-[640px] max-h-[88vh] overflow-hidden flex flex-col"
        data-testid="add-item-dialog"
      >
        <DialogHeader>
          <DialogTitle className="fc-heading text-[#1C1C1C]">
            Add an upgrade
          </DialogTitle>
          <DialogDescription className="text-xs text-[#A8A29E]">
            Pick from your Cost Library — rates already include any overrides
            and currency scaling — or build a custom line item.
          </DialogDescription>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab} className="flex-1 min-h-0 flex flex-col">
          <TabsList className="bg-sage-50 border border-sage-200 rounded-md p-1 self-start">
            <TabsTrigger
              value="library"
              data-testid="add-item-tab-library"
              className="data-[state=active]:bg-white data-[state=active]:text-[#1C1C1C] text-xs px-3 h-7"
            >
              <Library className="w-3.5 h-3.5 mr-1.5" />
              From Library
            </TabsTrigger>
            <TabsTrigger
              value="custom"
              data-testid="add-item-tab-custom"
              className="data-[state=active]:bg-white data-[state=active]:text-[#1C1C1C] text-xs px-3 h-7"
            >
              <PenLine className="w-3.5 h-3.5 mr-1.5" />
              Custom
            </TabsTrigger>
          </TabsList>

          <TabsContent value="library" className="flex-1 min-h-0 mt-4 flex flex-col">
            <div className="relative">
              <Search className="w-3.5 h-3.5 text-[#A8A29E] absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              <Input
                data-testid="add-item-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={`Search ${room?.room_type || "room"} library and common upgrades…`}
                className="fc-input h-9 pl-9"
              />
            </div>

            <div
              className="mt-3 overflow-y-auto pr-1 -mr-1 space-y-4"
              style={{ maxHeight: "48vh" }}
              data-testid="add-item-library-list"
            >
              <Section
                label={`${roomLabel(room?.room_type)} library`}
                hint="Items typical to this room type, with your overrides applied."
                items={visibleRoomLibrary}
                onPick={addFromLibrary}
                fmt={fmt}
                emptyMsg={search ? "No matches in this room's library." : "No library items for this room type yet."}
              />
              <Section
                label="Common upgrades"
                hint="Items from other room types — e.g. add Demolition to a laundry, or Tiling to a bedroom."
                items={visibleCommon}
                onPick={addFromLibrary}
                fmt={fmt}
                emptyMsg={search ? "No matches in common upgrades." : "All common upgrades are already in this room's library."}
                testIdSuffix="common"
              />
            </div>
          </TabsContent>

          <TabsContent value="custom" className="flex-1 mt-4">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Name" colSpan={2}>
                <Input
                  data-testid="add-item-custom-name"
                  value={draft.name}
                  onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                  placeholder="e.g. Custom feature wall"
                  className="fc-input h-10"
                />
              </Field>
              <Field label="Kind">
                <SelectWrap
                  tid="add-item-custom-kind"
                  value={draft.kind}
                  onChange={(v) => setDraft({ ...draft, kind: v })}
                  options={KIND_OPTIONS}
                />
              </Field>
              <Field label="Unit">
                <SelectWrap
                  tid="add-item-custom-unit"
                  value={draft.unit}
                  onChange={(v) => setDraft({ ...draft, unit: v })}
                  options={UNIT_OPTIONS.map((u) => ({ value: u, label: u }))}
                />
              </Field>
              <Field label="Quantity">
                <Input
                  data-testid="add-item-custom-qty"
                  type="number"
                  value={draft.quantity}
                  onChange={(e) => setDraft({ ...draft, quantity: e.target.value })}
                  className="fc-input h-10"
                />
              </Field>
              <Field label={`Rate (${currency})`}>
                <Input
                  data-testid="add-item-custom-rate"
                  type="number"
                  value={draft.rate}
                  onChange={(e) => setDraft({ ...draft, rate: e.target.value })}
                  className="fc-input h-10"
                />
              </Field>
            </div>

            <div className="mt-5 flex items-center justify-between text-xs text-[#78716C]">
              <span>
                Total: <span className="fc-mono text-[#1C1C1C]">{fmt((Number(draft.quantity) || 0) * (Number(draft.rate) || 0))}</span>
              </span>
              <Button
                onClick={addCustom}
                disabled={!draft.name.trim()}
                data-testid="add-item-custom-submit"
                className="bg-[#1C1C1C] text-white hover:bg-[#2A2A2E] rounded-lg h-9 px-3.5 text-sm"
              >
                <Plus className="w-3.5 h-3.5 mr-1.5" /> Add custom item
              </Button>
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children, colSpan = 1 }) {
  return (
    <div className={colSpan === 2 ? "col-span-2" : ""}>
      <div className="fc-label mb-1.5">{label}</div>
      {children}
    </div>
  );
}

function SelectWrap({ tid, value, onChange, options }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={tid} className="fc-input h-10 rounded-md">
        <SelectValue />
      </SelectTrigger>
      <SelectContent className="bg-white border-black/[0.08] text-[#27272A]">
        {options.map((o) => (
          <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function capitalise(s) { return (s || "").charAt(0).toUpperCase() + (s || "").slice(1); }

function roomLabel(roomType) {
  const match = ROOM_TYPES.find((r) => r.value === roomType);
  return match ? match.label : "Room";
}

function Section({ label, hint, items, onPick, fmt, emptyMsg, testIdSuffix }) {
  return (
    <div data-testid={`add-item-section-${testIdSuffix || "room"}`}>
      <div className="flex items-baseline justify-between mb-1.5">
        <div className="fc-label">{label}</div>
        <div className="text-[11px] text-[#A8A29E]">{items.length} {items.length === 1 ? "item" : "items"}</div>
      </div>
      {hint && <p className="text-[11px] text-[#78716C] mb-2">{hint}</p>}
      {items.length === 0 ? (
        <div className="text-xs text-[#A8A29E] text-center py-6 border border-dashed border-black/[0.06] rounded-md">
          {emptyMsg}
        </div>
      ) : (
        <div className="space-y-1.5">
          {items.map((i) => (
            <ItemRow key={`${i.source}-${i.name}`} item={i} onPick={onPick} fmt={fmt} />
          ))}
        </div>
      )}
    </div>
  );
}

function ItemRow({ item: i, onPick, fmt }) {
  return (
    <button
      type="button"
      disabled={i.alreadyAdded}
      onClick={() => !i.alreadyAdded && onPick(i)}
      data-testid={`add-item-library-${i.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
      className={`w-full text-left rounded-lg border px-3.5 py-2.5 transition-colors flex items-center gap-3 ${
        i.alreadyAdded
          ? "bg-[#FAFAF9] border-black/[0.04] cursor-not-allowed opacity-60"
          : "bg-white border-black/[0.08] hover:border-sage-700 hover:bg-sage-50"
      }`}
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="text-sm font-medium text-[#1C1C1C] truncate">{i.name}</div>
          {i.source === "custom" && (
            <span className="text-[9px] uppercase tracking-[0.15em] bg-sage-100 text-sage-700 border border-sage-200 rounded-full px-1.5 py-0.5">
              Your library
            </span>
          )}
          {i.source === "common" && (
            <span className="text-[9px] uppercase tracking-[0.15em] bg-black/[0.04] text-[#78716C] border border-black/10 rounded-full px-1.5 py-0.5">
              Common
            </span>
          )}
          {i.alreadyAdded && (
            <span className="text-[9px] uppercase tracking-[0.15em] bg-black/5 text-[#78716C] border border-black/10 rounded-full px-1.5 py-0.5">
              Already added
            </span>
          )}
        </div>
        <div className="text-[11px] text-[#78716C] mt-0.5">
          {capitalise(i.kind)} · per {i.unit}
          {i.suggestedQty ? ` · default qty ${i.suggestedQty}` : ""}
        </div>
      </div>
      <div className="text-right shrink-0">
        <div className="text-sm fc-mono text-[#1C1C1C]">{fmt(i.rate)}</div>
        <div className="text-[10px] uppercase tracking-[0.15em] text-[#A8A29E]">/ {i.unit}</div>
      </div>
      {!i.alreadyAdded && <Plus className="w-4 h-4 text-sage-700 shrink-0" />}
    </button>
  );
}

// Suppress lint: ROOM_TYPES is exported in case downstream consumers need it.
export { ROOM_TYPES };
